import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { getCart } from '@/api';

interface NavItem {
  path: string;
  icon: string;
  label: string;
}

export default function BottomNav({ isDarkMode }: { isDarkMode: boolean }) {
  const location = useLocation();
  const { t } = useTranslation();
  const [cartCount, setCartCount] = useState(0);
  const navRef = useRef<HTMLDivElement>(null);
  const [activeX, setActiveX] = useState(0);
  const [navWidth, setNavWidth] = useState(0);

  useEffect(() => {
    const update = () => {
      if (navRef.current) {
        setNavWidth(navRef.current.offsetWidth);
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const loadCartCount = async () => {
      try {
        const data = await getCart();
        const items = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
            ? data
            : [];
        const count = items.reduce(
          (sum: number, item: any) => sum + (item.quantity || 1),
          0,
        );
        setCartCount(count);
      } catch {
        setCartCount(0);
      }
    };
    loadCartCount();
    const handleCartUpdate = () => loadCartCount();
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const navItems: NavItem[] = [
    { path: '/', icon: 'ri-home-5-line', label: t('nav.home') },
    { path: '/categories', icon: 'ri-grid-line', label: t('nav.categories') },
    { path: '/cart', icon: 'ri-shopping-cart-line', label: t('nav.cart') },
    { path: '/orders', icon: 'ri-file-list-3-line', label: t('nav.orders') },
    { path: '/profile', icon: 'ri-user-line', label: t('nav.profile') },
  ];

  const activeIndex = navItems.findIndex(
    (item) => item.path === location.pathname,
  );
  const currentIndex = activeIndex >= 0 ? activeIndex : 0;

  useEffect(() => {
    const total = navItems.length;
    if (navWidth && total) {
      const itemWidth = navWidth / total;
      setActiveX(currentIndex * itemWidth + itemWidth / 2);
    }
  }, [currentIndex, navWidth, navItems.length]);

  const isCart = (item: NavItem) => item.path === '/cart';

  // Adaptive glass: light = warm cream glass, dark = dark charcoal glass
  const glassBg = isDarkMode
    ? 'rgba(24, 22, 20, 0.78)'
    : 'rgba(255, 252, 247, 0.72)';
  const borderColor = isDarkMode
    ? 'rgba(255, 255, 255, 0.08)'
    : 'rgba(202, 165, 62, 0.15)';
  const shineColor = isDarkMode
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(255, 255, 255, 0.6)';
  const shadowColor = isDarkMode
    ? '0 8px 32px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.06)'
    : '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
  const labelColor = isDarkMode ? 'text-stone-100' : 'text-stone-500';
  const iconColor = isDarkMode ? 'text-stone-100' : 'text-stone-500';

  return (
    <>
      <div style={{ height: 64 }} className="lg:hidden" />

      <nav
        ref={navRef}
        className="fixed bottom-0 left-0 right-0 z-50 lg:hidden select-none"
        style={{
          paddingBottom: 'max(0px, env(safe-area-inset-bottom))',
        }}
      >
        {/* Glass container */}
        <div
          className="relative mx-3 rounded-2xl overflow-hidden"
          style={{
            background: glassBg,
            backdropFilter: 'blur(32px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(32px) saturate(1.4)',
            border: `1px solid ${borderColor}`,
            boxShadow: shadowColor,
          }}
        >
          {/* Top inner shine — glass edge */}
          <div
            className="absolute inset-0 pointer-events-none rounded-2xl"
            style={{
              background: `linear-gradient(180deg, ${shineColor} 0%, transparent 40%, transparent 100%)`,
            }}
          />

          {/* Active glow indicator */}
          <motion.div
            className="absolute top-2 pointer-events-none"
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'rgba(202, 165, 62, 0.10)',
              boxShadow: '0 0 20px rgba(202, 165, 62, 0.20)',
              x: -20,
              left: activeX,
            }}
            animate={{ left: activeX }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 30,
              mass: 0.8,
            }}
          />

          {/* Top gold accent line */}
          <div
            className="absolute top-0 left-4 right-4 h-px"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(202,165,62,0.25) 50%, transparent 100%)',
            }}
          />

          {/* Nav items */}
          <div className="relative flex items-center justify-around py-2 px-1">
            {navItems.map((item, index) => {
              const isActive = index === currentIndex;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className="relative flex flex-col items-center justify-center gap-0.5 group"
                  style={{ width: 56, height: 56 }}
                  {...(isCart(item) ? { 'data-cart-nav': 'bottom' } : {})}
                >
                  {/* Icon */}
                  <div className="relative flex items-center justify-center w-6 h-6">
                    <motion.i
                      className={`${item.icon} text-[22px] ${isActive ? 'text-gold' : iconColor}`}
                      animate={{
                        scale: isActive ? 1.1 : 1,
                        y: isActive ? -1 : 0,
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 400,
                        damping: 24,
                        mass: 0.5,
                      }}
                    />

                    {/* Cart badge */}
                    {isCart(item) && cartCount > 0 && (
                      <AnimatePresence>
                        <motion.span
                          className="absolute -top-2 -right-3.5 bg-gold rounded-full text-[9px] flex items-center justify-center text-stone-900 font-bold leading-none"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          exit={{ scale: 0 }}
                          style={{ minWidth: 16, height: 16, padding: '0 4px' }}
                        >
                          {cartCount > 99 ? '99+' : cartCount}
                        </motion.span>
                      </AnimatePresence>
                    )}

                    {/* Active dot */}
                    {isActive && (
                      <motion.div
                        className="absolute -bottom-0.5 w-1 h-1 rounded-full bg-gold"
                        layoutId="bottom-nav-dot"
                        transition={{
                          type: 'spring',
                          stiffness: 400,
                          damping: 30,
                        }}
                      />
                    )}
                  </div>

                  {/* Label */}
                  <span
                    className={`text-[9px] font-medium leading-tight whitespace-nowrap ${isActive ? 'text-gold' : labelColor}`}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
}